import { Formik, FormikHelpers } from 'formik';

import { AuthSignInPayload } from 'modules/auth/domain/interfaces/auth-sign-in-payload';
import { useCallback } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useCountdown } from 'shared/hooks/use-countdown';
import { theme } from 'styles/theme/theme';
import UDFormInput from 'ud-form/components/input';
import UDInputPhone from 'ud-form/components/input/phone';
import UDButton from 'ud-ui/components/button';
import { SchemaOf } from 'yup';

interface SignInFormProps {
  initialValues: {
    username: string;
    password: string;
  };
  /** Проверка сессии после входа — запрос идёт не из сабмита; сам сабмит запирает Formik по промису `onSubmit`. */
  isLoading: boolean;
  /** Возвращает промис входа: «Войти» заперта до ответа (IMGM-1561). */
  onSubmit: (values: AuthSignInPayload, helpers: FormikHelpers<AuthSignInPayload>) => Promise<unknown>;
  validationSchema?: SchemaOf<AuthSignInPayload>;
  onUsernameChange?: (value: string) => void; // Новый проп для обработки изменения телефона
  /** Возвращает промис запроса кода: «Отправить код повторно» заперта до ответа (IMGM-1561). */
  onResendCode?: () => Promise<unknown>;
  isEmailCode?: boolean;
}

const SignInForm = (props: SignInFormProps) => {
  const { t } = useLingui();

  // Используем хук useCountdown для управления таймером
  const {
    seconds: resendTimeout,
    isActive: isResendDisabled,
    start: startResendTimer,
  } = useCountdown({
    initialSeconds: 30,
    autoStart: true, // Запускаем таймер при монтировании компонента
  });

  // Обработчик нажатия на кнопку повторной отправки кода
  // Таймер — сразу (он и гасит кнопку на 30 с), промис запроса — кнопке: раньше он терялся, и dev-детектор писал
  // `[lost-promise]` (IMGM-1561). Отказ показывает тостом `onCodeRequest` страницы.
  const { onResendCode } = props;
  const handleResendCode = useCallback(() => {
    startResendTimer(30);
    return onResendCode?.();
  }, [onResendCode, startResendTimer]);

  return (
    <Formik
      initialValues={props.initialValues}
      onSubmit={props.onSubmit}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={props.validationSchema}
    >
      {(formProps) => {
        return (
          <form onSubmit={formProps.handleSubmit}>
            <UDInputPhone
              style={{
                color: theme.colors.DarkBlue,
              }}
              name="username"
              label={t`Номер телефона`}
              placeholder={t`номер телефона`}
              wrapperProps={{ className: 'mb-3' }}
              value={formProps.values.username}
              onChange={(value) => {
                formProps.setFieldValue('username', value);
                // Вызываем обработчик изменения телефона, если он передан
                if (props.onUsernameChange) {
                  props.onUsernameChange(value);
                }
              }}
            />
            <UDFormInput
              autoFocus
              autoComplete="off"
              name="password"
              type="password"
              placeholder={props.isEmailCode ? t`Введите код из email` : t`Введите код из СМС`}
              containerProps={{ className: 'mb-4.5' }}
            />
            <UDButton
              variant="primary"
              type="submit"
              className="mb-2.5 w-full"
              style={{ fontFamily: 'SF Pro Display', fontWeight: 300 }}
              loading={props.isLoading}
            >
              {t`Войти`}
            </UDButton>
            <UDButton
              variant="primary"
              type="button"
              className="w-full"
              style={{ fontFamily: 'SF Pro Display', fontWeight: 300 }}
              loading={props.isLoading && !isResendDisabled}
              disabled={isResendDisabled}
              onClick={handleResendCode}
            >
              {isResendDisabled ? `${t`Отправить код повторно`} (${resendTimeout})` : t`Отправить код повторно`}
            </UDButton>
          </form>
        );
      }}
    </Formik>
  );
};

export default SignInForm;
